"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { GERMAN_ANNUAL_ALLOWANCE_EUR } from "@/lib/dividend-tracker/aggregation";
import { createClient } from "@/lib/supabase/client";

/** Inline-editierbarer, persistenter Steuerfreibetrag-Status (Nutzer-Feedback
 * 2026-07-18) - wie viel des jaehrlichen Sparerpauschbetrags (1.000 €/Person) bereits
 * anderweitig (ausserhalb dieser App) verbraucht ist. Speichert erst beim Verlassen
 * des Felds (gleiches Muster wie target-weight-input.tsx), direkt auf `profiles`, da
 * eine Person-weite Einstellung, keine je-Depot-/je-Waehrung-Einstellung. */
export function TaxAllowanceInput({ userId, value }: { userId: string; value: number }) {
  const router = useRouter();
  const [draft, setDraft] = useState(String(value));
  const [isPending, setIsPending] = useState(false);

  async function handleBlur() {
    const parsed = Number(draft);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setDraft(String(value));
      return;
    }
    if (parsed === value) return;

    setIsPending(true);
    const supabase = createClient();
    const { error } = await supabase
      .from("profiles")
      .update({ tax_allowance_used_eur: parsed })
      .eq("id", userId);
    setIsPending(false);

    if (!error) {
      router.refresh();
    } else {
      setDraft(String(value));
    }
  }

  return (
    <label className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        Bereits genutzter Steuerfreibetrag
        <Tooltip>
          <TooltipTrigger asChild>
            <button type="button" aria-label="Erklärung: Steuerfreibetrag" className="text-primary hover:text-primary/80">
              <Info className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-72">
            Der jährliche Sparerpauschbetrag liegt bei {GERMAN_ANNUAL_ALLOWANCE_EUR} € pro Person. Trag hier
            ein, wie viel davon du (z. B. über andere Konten/Broker) bereits für dieses Jahr verbraucht hast
            - der Rest mindert automatisch den steuerpflichtigen Anteil deiner hier erfassten Dividenden,
            bevor die Kapitalertragsteuer+Soli-Näherung (26,375 %) angewendet wird. Näherung, keine
            Steuerberatung.
          </TooltipContent>
        </Tooltip>
      </span>
      <Input
        type="number"
        min="0"
        max={GERMAN_ANNUAL_ALLOWANCE_EUR}
        step="10"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        disabled={isPending}
        aria-label="Bereits genutzter Steuerfreibetrag in Euro"
        className="w-24 bg-card text-right"
      />
      <span>€ von {GERMAN_ANNUAL_ALLOWANCE_EUR} €</span>
    </label>
  );
}
